import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2, Utensils, Camera, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"];
const empty = { name: "", meal_type: "breakfast", calories: "", protein_g: "", carbs_g: "", fat_g: "" };

const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseLocal = (s) => new Date(s + "T00:00:00");

// Downscale an image file to a JPEG data URL to keep the vision payload small.
const fileToDataUrl = (file, max = 1024) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function MealTracker() {
  const { user } = useAuth();
  const today = toDateStr(new Date());
  const [viewDate, setViewDate] = useState(today);
  const [calOpen, setCalOpen] = useState(false);
  const [data, setData] = useState({ meals: [], totals: { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 } });
  const [target, setTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [desc, setDesc] = useState("");
  const [estimating, setEstimating] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isToday = viewDate === today;

  const load = useCallback(() => {
    api.get("/progress/meals", { params: { date: viewDate } }).then(({ data }) => setData(data)).catch(() => {});
  }, [viewDate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user?.age && user?.height_in && user?.weight_lb) {
      api.post("/nutrition/calculate", {
        age: user.age, sex: user.sex || "male", height_in: user.height_in,
        weight_lb: user.weight_lb, activity_level: "moderate", goal: user.goal || "maintain", rate: "moderate",
      }).then(({ data }) => setTarget(data)).catch(() => {});
    }
  }, [user]);

  const shiftDay = (delta) => {
    const d = parseLocal(viewDate);
    d.setDate(d.getDate() + delta);
    const s = toDateStr(d);
    if (s > today) return; // don't go into the future
    setViewDate(s);
  };

  const estimate = async () => {
    if (!desc.trim()) return;
    setEstimating(true);
    try {
      const { data } = await api.post("/nutrition/estimate-meal", { description: desc });
      setForm({ name: data.name || desc, meal_type: form.meal_type, calories: data.calories, protein_g: data.protein_g, carbs_g: data.carbs_g, fat_g: data.fat_g });
      toast.success("Estimated — review and save");
    } catch (e) { toast.error("Could not estimate"); }
    finally { setEstimating(false); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoLoading(true);
    setOpen(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const { data } = await api.post("/nutrition/estimate-meal-photo", { image_base64: dataUrl });
      setForm({ name: data.name || "Meal", meal_type: form.meal_type, calories: data.calories, protein_g: data.protein_g, carbs_g: data.carbs_g, fat_g: data.fat_g });
      if (!data.calories) toast.info("Couldn't read that clearly — enter details manually.");
      else toast.success("Meal analyzed — review and save");
    } catch (err) { toast.error("Could not analyze photo"); }
    finally { setPhotoLoading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/progress/meal", {
        name: form.name, meal_type: form.meal_type,
        calories: parseInt(form.calories) || 0, protein_g: parseInt(form.protein_g) || 0,
        carbs_g: parseInt(form.carbs_g) || 0, fat_g: parseInt(form.fat_g) || 0,
        date: viewDate,
      });
      toast.success("Meal logged");
      setForm(empty); setDesc(""); setOpen(false); load();
    } catch (e) { toast.error("Could not log meal"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/progress/meal/${id}`); load(); } catch (e) {}
  };

  const t = data.totals;
  const pct = (v, goal) => (goal ? Math.min(100, Math.round((v / goal) * 100)) : 0);
  const prettyDate = parseLocal(viewDate).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  return (
    <div className="space-y-4 mt-4" data-testid="meal-tracker">
      {/* Day selector for recalling previous days */}
      <div className="flex items-center gap-2">
        <button data-testid="meal-prev-day" onClick={() => shiftDay(-1)} className="h-9 w-9 flex items-center justify-center rounded-xl bg-card border border-border text-muted-foreground hover:text-[#ccff0c]"><ChevronLeft className="h-4 w-4" /></button>
        <Popover open={calOpen} onOpenChange={setCalOpen}>
          <PopoverTrigger asChild>
            <button data-testid="meal-date-picker" className="flex-1 h-9 flex items-center justify-center gap-2 rounded-xl bg-card border border-border text-sm font-medium hover:border-[#ccff0c]/40">
              <CalendarDays className="h-4 w-4 text-[#ccff0c]" /> {isToday ? "Today" : prettyDate}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-popover border-border" align="center">
            <Calendar
              mode="single"
              selected={parseLocal(viewDate)}
              onSelect={(d) => { if (d) { const s = toDateStr(d); if (s <= today) setViewDate(s); } setCalOpen(false); }}
              disabled={(d) => d > new Date()}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        <button data-testid="meal-next-day" onClick={() => shiftDay(1)} disabled={isToday} className="h-9 w-9 flex items-center justify-center rounded-xl bg-card border border-border text-muted-foreground hover:text-[#ccff0c] disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
      </div>

      {/* Daily summary vs target */}
      <Card className="rounded-2xl bg-card border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{isToday ? "Today's intake" : "Intake"}</p>
          <span className="text-xs text-muted-foreground" data-testid="meal-view-date">{isToday ? data.date : prettyDate}</span>
        </div>
        {[
          ["Calories", t.calories, target?.target_calories, ""],
          ["Protein", t.protein_g, target?.protein_g, "g"],
          ["Carbs", t.carbs_g, target?.carb_g, "g"],
          ["Fat", t.fat_g, target?.fat_g, "g"],
        ].map(([label, val, goal, unit]) => (
          <div key={label} className="mb-3 last:mb-0">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-mono">{val}{unit}{goal ? <span className="text-muted-foreground"> / {goal}{unit}</span> : null}</span>
            </div>
            {goal ? <Progress value={pct(val, goal)} className="h-1.5 bg-secondary" /> : null}
          </div>
        ))}
        {!target && <p className="text-[11px] text-muted-foreground mt-1">Complete your profile (age, height, weight) to see targets.</p>}
      </Card>

      {/* Photo -> AI logging */}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid="meal-photo-input" />
      <Button data-testid="meal-photo-button" onClick={() => fileRef.current?.click()} disabled={photoLoading} className="w-full h-11 bg-card border border-[#ccff0c]/30 text-[#ccff0c] hover:bg-[#ccff0c]/10 font-semibold">
        {photoLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />} Snap a photo of your meal
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="add-meal-button" className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold"><Plus className="h-4 w-4 mr-1" /> Log a meal{!isToday ? ` · ${prettyDate}` : ""}</Button>
        </DialogTrigger>
        <DialogContent className="bg-popover border-border max-w-[420px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{isToday ? "Log a meal" : `Log a meal · ${prettyDate}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl bg-[#ccff0c]/10 border border-[#ccff0c]/20 p-3">
              <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-[#ccff0c]" /><p className="text-xs font-semibold text-[#ccff0c]">Estimate with AI</p></div>
              <div className="flex gap-2">
                <Input data-testid="meal-estimate-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. 2 eggs, toast & butter" className="bg-card border-border h-10" />
                <Button data-testid="meal-estimate-button" onClick={estimate} disabled={estimating || !desc.trim()} className="h-10 bg-secondary border border-border text-foreground flex-shrink-0">{estimating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Estimate"}</Button>
              </div>
              <Button data-testid="meal-photo-button-dialog" onClick={() => fileRef.current?.click()} disabled={photoLoading} className="w-full mt-2 h-9 bg-muted border border-border text-muted-foreground text-sm">
                {photoLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />} Or use a photo
              </Button>
            </div>
            <div><Label className="text-xs">Meal name</Label><Input data-testid="meal-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-card border-border h-10" /></div>
            <div><Label className="text-xs">Meal type</Label>
              <Select value={form.meal_type} onValueChange={(v) => set("meal_type", v)}>
                <SelectTrigger data-testid="meal-type" className="bg-card border-border h-10 capitalize"><SelectValue /></SelectTrigger>
                <SelectContent>{MEAL_TYPES.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Calories</Label><Input data-testid="meal-calories" type="number" value={form.calories} onChange={(e) => set("calories", e.target.value)} className="bg-card border-border h-10" /></div>
              <div><Label className="text-xs">Protein (g)</Label><Input data-testid="meal-protein" type="number" value={form.protein_g} onChange={(e) => set("protein_g", e.target.value)} className="bg-card border-border h-10" /></div>
              <div><Label className="text-xs">Carbs (g)</Label><Input data-testid="meal-carbs" type="number" value={form.carbs_g} onChange={(e) => set("carbs_g", e.target.value)} className="bg-card border-border h-10" /></div>
              <div><Label className="text-xs">Fat (g)</Label><Input data-testid="meal-fat" type="number" value={form.fat_g} onChange={(e) => set("fat_g", e.target.value)} className="bg-card border-border h-10" /></div>
            </div>
            <Button data-testid="meal-save" onClick={save} disabled={saving} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save meal"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-2">
        {data.meals.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-muted-foreground">
            <Utensils className="h-8 w-8 mb-2" />
            <p className="text-sm">No meals logged {isToday ? "today" : `on ${prettyDate}`}.</p>
          </div>
        ) : (
          data.meals.map((m) => (
            <Card key={m.id} data-testid={`meal-${m.id}`} className="rounded-xl bg-card border-border p-3 flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">{m.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{m.meal_type} · {m.calories} cal · P{m.protein_g} C{m.carbs_g} F{m.fat_g}</p>
              </div>
              <button data-testid={`meal-delete-${m.id}`} onClick={() => del(m.id)} className="text-muted-foreground hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
